import { Link } from '@inertiajs/react'
import type { ScoreboardMetric, ScoreboardStatus } from '../../types'
import { TrendingUp } from 'lucide-react'
import StatNumber from '../shared/StatNumber'

interface Props {
  scoreboard: ScoreboardMetric[]
  teamId: number | null
}

const STATUS_CONFIG: Record<ScoreboardStatus, { color: string; label: string; bgClass: string; textClass: string }> = {
  win: { color: 'text-status-win', label: 'WON', bgClass: 'bg-status-win/10', textClass: 'text-status-win' },
  miss: { color: 'text-status-miss', label: 'MISSED', bgClass: 'bg-status-miss/10', textClass: 'text-status-miss' },
  close: { color: 'text-status-warning', label: 'CLOSE', bgClass: 'bg-status-warning/10', textClass: 'text-status-warning' },
  no_data: { color: 'text-text-muted', label: 'NO DATA', bgClass: 'bg-[#5F6368]/10', textClass: 'text-text-muted' },
}

function formatValue(value: number | null, format: string | null): string {
  if (value === null || value === undefined) return '—'
  switch (format) {
    case 'currency':
      return `$${Math.round(value).toLocaleString()}`
    case 'percentage':
      return `${value}%`
    default:
      return value.toLocaleString()
  }
}

export default function WeeklyScoreboard({ scoreboard, teamId }: Props) {
  if (scoreboard.length === 0) {
    return (
      <section>
        <h2 className="text-base font-bold uppercase tracking-widest text-text-muted mb-4 font-sans">
          THIS WEEK&apos;S SCOREBOARD
        </h2>
        <div className="bg-white rounded-xl shadow-card border border-stone/10 p-8 text-center">
          <TrendingUp className="w-12 h-12 text-text-muted mx-auto mb-3" />
          <p className="text-xl font-semibold text-text-primary mb-2 font-sans">No metrics tracked yet</p>
          <p className="text-lg text-text-muted mb-4">Start tracking your team&apos;s key numbers to see the scoreboard come alive.</p>
          {teamId ? (
            <Link
              href={`/teams/${teamId}/scoreboard`}
              className="inline-block bg-pinnacle-red hover:bg-pinnacle-red/90 text-white font-semibold py-3 px-6 rounded-lg transition-colors text-base font-sans"
            >
              Set Up Metrics
            </Link>
          ) : (
            <Link
              href="/teams"
              className="inline-block bg-pinnacle-red hover:bg-pinnacle-red/90 text-white font-semibold py-3 px-6 rounded-lg transition-colors text-base font-sans"
            >
              Join a Team
            </Link>
          )}
        </div>
      </section>
    )
  }

  return (
    <section>
      <h2 className="text-base font-bold uppercase tracking-widest text-text-muted mb-4 font-sans">
        THIS WEEK&apos;S SCOREBOARD
      </h2>
      <div className="flex flex-wrap gap-4">
        {scoreboard.map((metric) => {
          const config = STATUS_CONFIG[metric.status]
          return (
            <div
              key={metric.id}
              className="bg-white rounded-xl shadow-card border border-stone/10 p-5 flex flex-col justify-between"
              style={{ flex: '1 1 180px', minWidth: 180, minHeight: 150 }}
            >
              <p className="text-lg font-semibold text-text-primary mb-1 font-sans truncate">{metric.name}</p>
              {metric.owner_name && (
                <p className="text-base font-semibold text-text-muted mb-2 truncate">{metric.owner_name}</p>
              )}
              <StatNumber size="lg" className={config.color}>
                {formatValue(metric.value, metric.format)}
              </StatNumber>
              <div className="flex items-center justify-between mt-2">
                <span
                  className={`text-sm font-bold uppercase tracking-wide px-2.5 py-1 rounded-full ${config.bgClass} ${config.textClass}`}
                >
                  {config.label}
                </span>
                {metric.target !== null && (
                  <span className="text-base font-semibold text-text-muted">
                    Target: {formatValue(metric.target, metric.format)}
                  </span>
                )}
              </div>
            </div>
          )
        })}
      </div>
    </section>
  )
}
